import React from 'react';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import PostCard from './PostCard';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarWeekView = ({
    currentDate,
    posts,
    integrations,
    onSlotClick,
    onPostClick,
    onPostEdit,
    onPostDelete,
    onPostPublish
}) => {
    // Get the start of the week (Sunday)
    const weekStart = dayjs(currentDate).startOf('week');

    // Generate array of days in the week
    const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));

    // Get integration by ID
    const getIntegration = (integrationId) => {
        return integrations.find(i => i.id === integrationId);
    };

    // Get posts for a specific day and hour
    const getPostsForSlot = (day, hour) => {
        const slotStart = day.hour(hour).minute(0).second(0);
        const slotEnd = slotStart.add(1, 'hour');

        return posts.filter(post => {
            if (!post.scheduled_at && !post.published_at) return false;
            const postDate = dayjs(post.scheduled_at || post.published_at);
            return postDate.isAfter(slotStart) && postDate.isBefore(slotEnd);
        });
    };

    // Check if a day is today
    const isToday = (day) => day.isSame(dayjs(), 'day');

    return (
        <div className="flex flex-col h-full">
            {/* Header row with day names */}
            <div className="flex border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
                {/* Time column header */}
                <div className="w-16 flex-shrink-0 p-2 border-r border-slate-200 dark:border-slate-700" />

                {/* Day headers */}
                {weekDays.map((day, index) => (
                    <div
                        key={day.format('YYYY-MM-DD')}
                        className={`flex-1 p-3 text-center border-r border-slate-200 dark:border-slate-700 last:border-r-0 ${
                            isToday(day) ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                        }`}
                    >
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            {DAYS[index]}
                        </p>
                        <p className={`text-lg font-semibold mt-1 ${
                            isToday(day)
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-slate-900 dark:text-white'
                        }`}>
                            {day.format('D')}
                        </p>
                    </div>
                ))}
            </div>

            {/* Time grid */}
            <div className="flex-1 overflow-auto">
                <div className="flex">
                    {/* Time labels column */}
                    <div className="w-16 flex-shrink-0 border-r border-slate-200 dark:border-slate-700">
                        {HOURS.map((hour) => (
                            <div
                                key={hour}
                                className="h-20 border-b border-slate-100 dark:border-slate-800 flex items-start justify-end pr-2 pt-1"
                            >
                                <span className="text-xs text-slate-400 dark:text-slate-500">
                                    {dayjs().hour(hour).format('h A')}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Day columns */}
                    {weekDays.map((day) => (
                        <div
                            key={day.format('YYYY-MM-DD')}
                            className={`flex-1 border-r border-slate-200 dark:border-slate-700 last:border-r-0 ${
                                isToday(day) ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''
                            }`}
                        >
                            {HOURS.map((hour) => {
                                const slotPosts = getPostsForSlot(day, hour);
                                const slotTime = day.hour(hour).minute(0);

                                return (
                                    <div
                                        key={hour}
                                        onClick={() => onSlotClick?.(slotTime.toISOString())}
                                        className="h-20 border-b border-slate-100 dark:border-slate-800 p-1 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                    >
                                        <div className="space-y-1">
                                            {slotPosts.slice(0, 2).map((post) => (
                                                <PostCard
                                                    key={post.id}
                                                    post={post}
                                                    integration={getIntegration(post.integration_id)}
                                                    compact
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onPostClick?.(post);
                                                    }}
                                                />
                                            ))}
                                            {slotPosts.length > 2 && (
                                                <div className="text-xs text-slate-500 dark:text-slate-400 text-center py-1">
                                                    +{slotPosts.length - 2} more
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CalendarWeekView;
